// ============================================================================
// dataTypes/liveStreamManagment/sourceRegistry/types.ts
// Types unifiés du système de sources.
//
// Principe : "Tout est une Source". Chaque source produit un MediaStream
// (vidéo+audio, vidéo-only, ou audio-only) et suit un cycle de vie strict :
//   idle → acquiring → active → idle (ou error)
//
// Le SourceRegistry est l'unique propriétaire des flux. Personne d'autre
// n'appelle getUserMedia, getDisplayMedia, ou ne crée de <video> caché.
// ============================================================================

// ─── État du cycle de vie ─────────────────────────────────────────────────────

export type SourceState = 'idle' | 'acquiring' | 'active' | 'error';

// ─── Types de sources ─────────────────────────────────────────────────────────

export type SourceType =
  | 'device'    // webcam locale
  | 'screen'    // partage d'écran
  | 'hls'       // flux HLS/DASH/natif depuis URL
  | 'remote'    // flux WebRTC entrant (caméra distante)
  | 'jingle'    // vidéo+audio depuis fichier (lecture éphémère)
  | 'sound'     // audio-only depuis fichier
  | 'scene'     // composition de N sources via canvas
  | 'final';    // flux composité en cours de diffusion (canvas de régie)

// Types de sources LOCALES au poste — coûteuses ou impossibles à rétablir sans
// action de l'opérateur (webcam : redemande le périphérique et refait la course
// d'ouverture ; écran : redemande une autorisation navigateur). Le registre vit à la
// racine de l'app (depuis le 2026-08-28) précisément pour qu'elles survivent à un
// changement de chaîne — tout code qui libère des emprunts en masse (démontage,
// nettoyage de fin de session de chaîne...) doit les épargner, sur ce même critère.
export const PERSISTENT_SOURCE_TYPES: readonly SourceType[] = ['device', 'screen'];

// ─── Configuration spécifique par type ────────────────────────────────────────

export interface DeviceSourceConfig {
  type: 'device';
  deviceId: string;
  video?: {
    width?: number;
    height?: number;
    frameRate?: number;
    facingMode?: string;
  };
}

export interface ScreenSourceConfig {
  type: 'screen';
  frameRate?: number;
  width?: number;
  height?: number;
  cursor?: 'always' | 'motion' | 'never';
  withAudio?: boolean;
}

export interface XtreamCredentials {
  server:   string;
  username: string;
  password: string;
  streamId: number;
}

export interface HlsSourceConfig {
  type      : 'hls';
  url       : string;
  xtream?   : XtreamCredentials;
  loop?     : boolean;  // relance le flux HLS quand il se termine
  autoRetry?: boolean;  // si true : recharge le manifest sans recréer la <video>
                        //           ni le MediaStream (pour les flux "libres" non-Xtream)
                        //           → le MonitorVideoPlayer ne reçoit jamais null
}

export interface RemoteSourceConfig {
  type: 'remote';
  /** device_id du device distant (ex: "phone-001") */
  remoteDeviceId: string;
}

export interface JingleSourceConfig {
  type: 'jingle';
  url: string;
  volume?: number;
  loop?: boolean;
}

export interface SoundSourceConfig {
  type: 'sound';
  url: string;
  volume?: number;
  loop?: boolean;
}

export interface SceneSourceConfig {
  type: 'scene';
  layoutId: string;
}

/**
 * Le flux composité (canvas de régie) actuellement à l'antenne. N'a pas de
 * paramètres propres : son stream est injecté par useCanvasPipeline à chaque
 * publication du canvas (cf. FinalAcquirer).
 */
export interface FinalSourceConfig {
  type: 'final';
}

export type SourceConfig =
  | DeviceSourceConfig
  | ScreenSourceConfig
  | HlsSourceConfig
  | RemoteSourceConfig
  | JingleSourceConfig
  | SoundSourceConfig
  | SceneSourceConfig
  | FinalSourceConfig;

// ─── Source unifiée ───────────────────────────────────────────────────────────

export interface Source {
  /** Identifiant unique, stable entre les sessions */
  id: string;
  /** Type de la source */
  type: SourceType;
  /** Label affiché dans l'UI */
  label: string;
  /** Configuration spécifique au type */
  config: SourceConfig;
  /** État courant du cycle de vie */
  state: SourceState;
  /** Le flux produit (null si idle ou error) */
  stream: MediaStream | null;
  /** Message d'erreur si state === 'error' */
  error?: string;
  /**
   * Nombre de consommateurs actifs (borrow).
   * Le registry ne release pas une source tant que refCount > 0.
   */
  refCount: number;
}

// ─── Événements émis par le registry ──────────────────────────────────────────

export type SourceEventType =
  | 'registered'
  | 'unregistered'
  | 'state-changed'
  | 'stream-updated'
  | 'error';

export interface SourceEvent {
  type: SourceEventType;
  sourceId: string;
  source: Source;
  detail?: any;
}

export type SourceEventListener = (event: SourceEvent) => void;

// ─── Acquirer — interface que chaque type de source implémente ────────────────

export interface SourceAcquirer {
  /**
   * Acquiert le flux pour une source donnée.
   * Retourne le MediaStream produit.
   * Peut throw en cas d'erreur (permission refusée, réseau, etc.)
   */
  acquire(source: Source): Promise<MediaStream>;

  /**
   * Libère les ressources associées au flux.
   * Appelé par le registry quand refCount tombe à 0.
   */
  release(source: Source): void;
}

// ─── Handle retourné par borrow() ─────────────────────────────────────────────

export interface BorrowHandle {
  /** L'id de la source empruntée */
  sourceId: string;
  /** Le stream emprunté */
  stream: MediaStream;
  /** Libère l'emprunt (décrémente refCount) */
  release: () => void;
}